import { useMemo, useState } from "react"
import DeckSpread from "@/components/DeckBrowser/DeckSpread"
import JobEditModal from "@/components/JobEditModal/JobEditModal"
import JobStatement from "@/components/JobStatement/JobStatement"
import Pill from "@/components/Pill/Pill"
import RoundHeading from "@/components/RoundHeading/RoundHeading"
import HandSlots from "./HandSlots"
import SwipeDeal from "./SwipeDeal"
import { cardsInPlay } from "@/content/cards"
import { CARDS_COPY } from "@/content/cardsRound"
import { useCardHand } from "@/hooks/useCardHand"
import { useSessionStore } from "@/store/sessionStore"
import {
  peopleFor,
  picksBy,
  selectAmIDone,
  selectJob,
  selectMyAuthorId,
  selectPicks,
  selectSession,
} from "@/store/selectors"
import type { CardId } from "@/types"
import styles from "@/styles/pages/Cards.module.css"

/*
 * Everyone builds their own hand.
 *
 * One pick document per person (`jobs/{jobId}/picks/{participantId}`), so two
 * people taking the same card at the same instant cannot overwrite each other —
 * which a single shared array on the job would.
 *
 * The hand itself lives in `useCardHand`, not here: a card is added and removed
 * one at a time through a server-side array transform, and the screen shows the
 * decision before the listener echoes it. Writing `[...mine, cardId]` from this
 * component was the bug that made a fast deal lose cards.
 *
 * `handCap` is enforced by that hook and nowhere else. The service takes any
 * card and the rules cannot count, so the client is the cap: it is a rule of the
 * exercise, not a constraint of the data.
 */

interface CardsPickProps {
  canDrive: boolean
  /** The round is being read back: the hand is shown, and cannot be changed. */
  readOnly: boolean
}

export default function CardsPick({ canDrive, readOnly }: CardsPickProps) {
  const session = useSessionStore(selectSession)
  const job = useSessionStore(selectJob)
  const picks = useSessionStore(selectPicks)
  const participants = useSessionStore((s) => s.participants)
  const myAuthorId = useSessionStore(selectMyAuthorId)
  const amIDone = useSessionStore(selectAmIDone)
  const [chosenView, setView] = useState<"deal" | "spread">("deal")
  const [editing, setEditing] = useState(false)

  /*
   * Looking back at the pick, the deal is not the thing to show.
   *
   * The deal is a gesture — a card arrives, you throw it one way or the other —
   * and a deck that deals cards nobody may keep is a broken toy, not a record.
   * The spread is the same deck as a document: every card, with the ones in your
   * hand marked. So a review forces it, and the tabs that would switch back come
   * off.
   *
   * Marking your own hand done is a lock, not a jump: `amIDone` freezes the
   * deal and the spread's take/drop (see `PainsBoard`'s `votable && !amIDone`)
   * but leaves you on the view you were reading. Only the room's read-back
   * forces the spread.
   */
  const locked = readOnly || amIDone
  const view = readOnly ? "spread" : chosenView

  const cap = session?.config.handCap ?? 0
  const confirmed = useMemo(() => picksBy(picks, myAuthorId), [picks, myAuthorId])
  // Above the guard because hooks cannot be conditional; the empty ids are never
  // reached, since nothing below can call into the hand until both exist.
  const {
    hand: mine,
    capReached,
    keep,
    drop,
    toggle,
  } = useCardHand(session?.id ?? "", job?.id ?? "", myAuthorId, confirmed, cap)

  if (!session || !job) return null

  const deck = cardsInPlay(session.config.families)

  /*
   * Who nominated a card, on the card itself in the spread — facilitator only,
   * for the reason `PickMonitor` is: a picker seeing the others' hands mid-round
   * would anchor them.
   */
  const pickersFor = (cardId: CardId) => {
    const pick = picks.filter((entry) => entry.cardIds.includes(cardId))
    return peopleFor(
      participants,
      pick.map((entry) => entry.id)
    )
  }

  return (
    <div className={styles.pick}>
      <RoundHeading title={CARDS_COPY.heading} objective={CARDS_COPY.objective} />

      {/* The sentence the cards are being held against, kept on screen. The
          facilitator can fix a word here without stepping the room back. */}
      <JobStatement
        job={job}
        userStoryEnabled={session.config.userStoryEnabled}
        onEdit={canDrive && !readOnly ? () => setEditing(true) : undefined}
      />
      {editing ? (
        <JobEditModal
          sessionId={session.id}
          job={job}
          userStoryEnabled={session.config.userStoryEnabled}
          onClose={() => setEditing(false)}
        />
      ) : null}

      <div className={styles.picking}>
        {/* Full-width inside the paper, above the deck rather than beside it:
            the switch is over the whole surface the room is picking on, not a
            control bolted to one corner of the deal. */}
        {readOnly ? null : (
          <div className={styles.viewToggleRow}>
            <div className={styles.tabs} role="tablist">
              {(
                [
                  ["deal", CARDS_COPY.dealTab],
                  ["spread", CARDS_COPY.spreadTab],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={view === key}
                  className={view === key ? `${styles.tab} ${styles.tabOn}` : styles.tab}
                  onClick={() => setView(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.pickingMain}>
          {view === "deal" ? (
            /* The hand rides beside the deal here, not below it: dealing is a
                 one-card conversation, and the slots it is filling belong next
                 to it rather than under a card someone is still deciding on. */
            <div className={styles.dealLayout}>
              <div className={styles.dealMain}>
                <SwipeDeal
                  deck={deck}
                  picked={mine}
                  cap={cap}
                  capReached={capReached}
                  onKeep={keep}
                  onDrop={drop}
                  onSeeSpread={() => setView("spread")}
                  readOnly={locked}
                />
              </div>
              <HandSlots picked={mine} cap={cap} onDrop={toggle} readOnly={locked} layout="aside" />
            </div>
          ) : (
            <>
              {/* No `onToggle` is what puts the spread in read mode: the cards in
                   the hand still carry their mark, and the take/drop button on each
                   one is simply not there. */}
              <DeckSpread
                families={session.config.families}
                picked={mine}
                onToggle={locked ? undefined : toggle}
                capReached={capReached}
                pickersFor={canDrive ? pickersFor : undefined}
              />

              <HandSlots picked={mine} cap={cap} onDrop={toggle} readOnly={locked} />
            </>
          )}

          {capReached && !locked ? <Pill tone="warning">{CARDS_COPY.capReached(cap)}</Pill> : null}
        </div>
      </div>
    </div>
  )
}
